import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { EquipmentFlagGate } from "@/components/equipment/EquipmentFlagGate";
import { EquipmentFactsCard } from "@/components/equipment/EquipmentFactsCard";
import { EquipmentFormDialog } from "@/components/equipment/EquipmentFormDialog";
import { EquipmentHistory } from "@/components/equipment/EquipmentHistory";
import { EquipmentImages } from "@/components/equipment/EquipmentImages";
import { EquipmentStatusBadge } from "@/components/equipment/EquipmentStatusBadge";
import { EquipmentCalendar } from "@/components/equipment/EquipmentCalendar";
import { BookingDialog, type BookingDialogDefaults } from "@/components/equipment/BookingDialog";
import { useDepartments } from "@/hooks/use-departments";
import { useEquipmentAccess, useEquipmentCategories, useEquipmentItem } from "@/hooks/use-equipment";
import { useEquipmentItemBookings, useEquipmentUsage } from "@/hooks/use-equipment-bookings";
import { todayIso } from "@/lib/equipment/insurance";
import { EnrichmentDialog } from "@/components/equipment/EnrichmentDialog";
import { ReportDamageDialog } from "@/components/equipment/ReportDamageDialog";
import { ProductDocumentsCard } from "@/components/equipment/ProductDocumentsCard";
import { useEquipmentProduct, useProductMedia } from "@/hooks/use-equipment-products";

/**
 * /utstyr/:id (VIB-2917, skjerm B): fakta, deler og tilbehør, egne bilder og historikk.
 * Produktdata (VIB-2918): produsentbilde, Dokumenter og «Hent produktdata» fra modellen.
 * Service (VIB-2921): «Meld skade» for alle interne.
 */
export default function EquipmentDetailPage() {
  return (
    <EquipmentFlagGate>
      <EquipmentDetail />
    </EquipmentFlagGate>
  );
}

function EquipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const access = useEquipmentAccess();
  const { data: item, isLoading } = useEquipmentItem(access.isLoading ? undefined : id, access.canManage);
  const { data: categories = [] } = useEquipmentCategories();
  const { data: departments = [] } = useDepartments();
  const [editOpen, setEditOpen] = useState(false);
  const { data: bookingLines = [] } = useEquipmentItemBookings(item?.id);
  const { data: usage } = useEquipmentUsage();
  const [booking, setBooking] = useState<{ bookingId: string | null; defaults?: BookingDialogDefaults } | null>(null);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [damageOpen, setDamageOpen] = useState(false);
  const { data: product = null } = useEquipmentProduct(item?.product_id);
  const { data: productMedia = [] } = useProductMedia(item?.product_id);

  if (isLoading || access.isLoading) {
    return (
      <div className="page-container flex justify-center py-12">
        <SrIcon name="loader2" className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!item) {
    return (
      <div className="page-container animate-fade-in space-y-2">
        <h1 className="page-title">Utstyr</h1>
        <p className="text-muted-foreground">Fant ikke utstyret.</p>
        <Link to="/utstyr" className="text-primary hover:underline text-sm">Tilbake til utstyrslista</Link>
      </div>
    );
  }

  const category = categories.find((c) => c.id === item.category_id);
  const departmentName = departments.find((d) => d.id === item.department_id)?.name ?? "Ukjent avdeling";
  const categoryName = category?.name ?? "–";
  const bookable = item.bookable ?? category?.bookable ?? true;

  return (
    <div className="page-container animate-fade-in space-y-6">
      <nav aria-label="Brødsmuler" className="flex gap-1.5 text-sm text-muted-foreground">
        <Link to="/utstyr" className="hover:underline">Utstyr</Link>
        <span>/</span>
        <span>{categoryName}</span>
      </nav>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            {categoryName} · {departmentName}
          </p>
          <h1 className="font-display font-semibold text-[clamp(26px,3vw,36px)] tracking-[-0.01em]">{item.name}</h1>
          {product && (
            <p className="text-sm text-muted-foreground">
              Modell <span className="text-foreground">{product.brand_name} {product.model}</span>
              {product.product_url && (
                <>
                  {" · "}
                  <a href={product.product_url} target="_blank" rel="noreferrer" className="underline">
                    Produktside
                  </a>
                </>
              )}
            </p>
          )}
          {item.serial_numbers.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Serienummer <span className="font-mono">{item.serial_numbers.join(", ")}</span>
            </p>
          )}
          {item.comment && <p className="text-sm text-muted-foreground mt-1">{item.comment}</p>}
        </div>
        <div className="flex items-center gap-2">
          <EquipmentStatusBadge status={item.status} />
          {bookable && item.status === "ok" && (
            <Button onClick={() => setBooking({ bookingId: null, defaults: { itemId: item.id } })}>
              <SrIcon name="calendar" className="h-4 w-4" />
              Book
            </Button>
          )}
          {item.status !== "sold" && item.status !== "retired" && (
            <Button variant="outline" onClick={() => setDamageOpen(true)}>
              <SrIcon name="wrench" className="h-4 w-4" />
              Meld skade
            </Button>
          )}
          {access.canManage && (
            <Button variant="outline" onClick={() => setEnrichOpen(true)}>
              <SrIcon name="download" className="h-4 w-4" />
              Hent produktdata
            </Button>
          )}
          {access.canManage && (
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              <SrIcon name="edit" className="h-4 w-4" />
              Rediger
            </Button>
          )}
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <div className="space-y-6">
          <EquipmentImages
            equipmentId={item.id}
            name={item.name}
            canManage={access.canManage}
            productImageUrl={productMedia.find((m) => m.kind === "image")?.url}
          />
          <EquipmentCalendar
            lines={bookingLines}
            today={todayIso()}
            onDayClick={(day) =>
              bookable && item.status === "ok" && setBooking({ bookingId: null, defaults: { itemId: item.id, dateFrom: day } })
            }
            onBookingClick={(bookingId) => setBooking({ bookingId })}
          />
          <EquipmentHistory equipmentId={item.id} />
        </div>
        <div className="space-y-6">
          <EquipmentFactsCard
            item={item}
            categoryName={categoryName}
            departmentName={departmentName}
            bookable={bookable}
            showAmounts={access.canManage}
            usage={usage?.get(item.id)}
          />
          <ProductDocumentsCard product={product} media={productMedia} />
        </div>
      </div>
      <BookingDialog
        open={booking !== null}
        onOpenChange={(open) => !open && setBooking(null)}
        bookingId={booking?.bookingId ?? null}
        defaults={booking?.defaults}
      />
      <ReportDamageDialog open={damageOpen} onOpenChange={setDamageOpen} item={{ id: item.id, name: item.name }} />
      {access.canManage && (
        <EnrichmentDialog open={enrichOpen} onOpenChange={setEnrichOpen} product={product} equipmentId={item.id} autoStart />
      )}
      {access.canManage && (
        <EquipmentFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          item={item}
          categories={categories}
          departments={departments}
          defaultDepartmentId={access.departmentId}
        />
      )}
    </div>
  );
}
